import { Connectable, Subselect, TreeSelection } from "./tree-select";
import { TreeItem, TreeItemDelegate } from "./tree-item";

/**
 * Provides shared functionality when working with a list of tree items.
 * Intended for shared use between single select mode and multi select mode implementations. 
 */
export abstract class TreeItemSubselect<
    TValue,
    TSubselect extends Subselect<TValue>,
    TListener extends object,
> implements Subselect<TValue>, Connectable<TListener>, TreeItemDelegate {

    protected callbacks: TListener | null = null;

    constructor(
        public readonly element: HTMLElement,
        protected readonly items: TreeItem[],
    ) {}

    get name(): string {
        return this.element.getAttribute('name') || '';
    }

    abstract get value(): TValue | null;
    abstract set value(value: TValue | null);

    getSubselectContainer(selection: TreeSelection): HTMLElement | null {
        const selectedItem = this.getItem(selection.path.terminalValue);
        return selectedItem?.list?.content || null;
    }

    getItem(value: string | null): TreeItem | null {
        if (!value) {
            return null;
        }
        return this.items.find(item => item.value === value) || null;
    }

    connect(listener: TListener): void {
        this.callbacks = listener;
        this.items.forEach(item => item.connect(this));
    }

    disconnect(): void {
        this.callbacks = null;
        this.items.forEach(item => item.disconnect());
    }
    
    abstract transitionToSelectedState(item: TreeItem, selected: boolean): void;
    abstract transitionToExpandedState(item: TreeItem, expanded: boolean): void;
}
